import type { Ref } from "vue";

type UseLegacyAreaDialogNavigationParams = {
  blockList: Ref<Array<unknown> | null | undefined>;
  sectionBlockList: Ref<unknown | null | undefined>;
  cityBlockList?: Ref<unknown | null | undefined>;
  originalCityAddress: Ref<string>;
  originalSectionAddress: Ref<string>;
  unassignedBlockList?: Ref<Array<string | null | undefined>>;
  shouldBackToAddressDialog?: () => boolean;
  onBackToAddressDialog?: () => void | Promise<void>;
  onDefaultExit?: () => void | Promise<void>;
  clearUnassignedOnSectionStep?: boolean;
};

/**
 * DeliveryRequest/RegularDelivery 系のエリア選択ダイアログ戻る処理を共通化する。
 * 画面固有の副作用は callback で差し込む。
 */
export const useLegacyAreaDialogNavigation = (params: UseLegacyAreaDialogNavigationParams) => {
  const {
    blockList,
    sectionBlockList,
    cityBlockList,
    originalCityAddress,
    originalSectionAddress,
    unassignedBlockList,
    shouldBackToAddressDialog,
    onBackToAddressDialog,
    onDefaultExit,
    clearUnassignedOnSectionStep = false,
  } = params;

  const clearUnassigned = (): void => {
    if (unassignedBlockList) {
      unassignedBlockList.value = [];
    }
  };

  const hasBlockItems = (): boolean => {
    return !!blockList.value && blockList.value.length > 0;
  };

  const onClickAreaDialogReturn = async (): Promise<void> => {
    // 郵便番号入力ダイアログへ戻す条件（画面ごとの差分）を最優先で評価する。
    if (shouldBackToAddressDialog?.()) {
      await onBackToAddressDialog?.();
      return;
    }

    // 丁目一覧を表示中なら、ひとつ前の町域選択へ戻す。
    if (hasBlockItems()) {
      blockList.value = [];
      clearUnassigned();
      originalSectionAddress.value = "";
      return;
    }

    // 町域一覧を表示中なら、ひとつ前の市区町村選択へ戻す。
    if (sectionBlockList.value) {
      sectionBlockList.value = null;
      if (clearUnassignedOnSectionStep) {
        clearUnassigned();
      }
      originalCityAddress.value = "";
      // 市区町村候補がない場合（郵便番号検索で市区町村が確定済みの場合）は
      // 住所選択ダイアログへ戻る。
      if (cityBlockList && !cityBlockList.value) {
        await onDefaultExit?.();
      }
      return;
    }

    // 一覧表示がない状態は初期段階へ戻し、画面固有の終了処理を委譲する。
    blockList.value = [];
    clearUnassigned();
    sectionBlockList.value = null;
    originalSectionAddress.value = "";
    originalCityAddress.value = "";
    await onDefaultExit?.();
  };

  return {
    onClickAreaDialogReturn,
  };
};
